import React from 'react'
import { X, FileText, Building2, Award } from 'lucide-react'

const DOCUMENT_CATEGORIES = [
  {
    icon: Building2,
    title: 'Business registration (required section)',
    items: [
      'One official document proving the business/school exists: incorporation, LLP, society, trust, UDYAM, or GST registration certificate.',
      'GST registration counts as business registration—upload the GST certificate here and enter GSTIN as the registration number when applicable.',
    ],
  },
  {
    icon: FileText,
    title: 'Address proof',
    items: [
      'Utility bill, lease/rent agreement, property tax, or municipal certificate linking the business or venue to the address.',
    ],
  },
  {
    icon: Award,
    title: 'Yoga Alliance (optional — if RYS certified)',
    items: ['Yoga Alliance RYS certificate or registration confirmation.'],
  },
  {
    icon: FileText,
    title: 'Additional documents (optional)',
    items: ['Insurance, tax IDs, brochures, or other supporting materials.'],
  },
]

interface DocumentsRequiredModalProps {
  isOpen: boolean
  onClose: () => void
  checklistIntro?: React.ReactNode
}

const DocumentsRequiredModal: React.FC<DocumentsRequiredModalProps> = ({
  isOpen,
  onClose,
  checklistIntro,
}) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Verification documents checklist</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          <p className="text-sm text-gray-600 mb-2">
            {checklistIntro ?? (
              <>Upload the same document types a yoga school would submit from their dashboard.</>
            )}
          </p>
          <p className="text-sm text-gray-600 mb-6 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
            <strong className="text-amber-900">Minimum:</strong> at least one file (PDF or image, max 2MB each).
            For faster approval, include <strong>business registration</strong> and <strong>address proof</strong>.
          </p>
          <div className="space-y-6">
            {DOCUMENT_CATEGORIES.map((category) => {
              const Icon = category.icon
              return (
                <div key={category.title}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <Icon className="h-4 w-4 text-amber-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">{category.title}</h3>
                  </div>
                  <ul className="space-y-2 pl-1">
                    {category.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-amber-500 mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DocumentsRequiredModal
